import { supabase } from '../supabase';
import { emitMfaRequired } from '../utils/auth/mfaRequiredSignal';
import { supabaseUrl, supabaseAnonKey } from '../config/supabaseConfig';
import { trackRequest } from '../utils/app/pendingWrites';

export async function getCurrentUser() {
  const { data, error } = await supabase.auth.getUser();
  return { user: data?.user ?? null, error };
}

export async function getProfile(userId) {
  const { data, error } = await supabase.from('profiles').select('full_name').eq('id', userId).single();
  return { data, error };
}

export async function getSession() {
  const { data } = await supabase.auth.getSession()
  return data?.session
}

/**
 * Status used when there was no HTTP response at all — the request never left,
 * or the network dropped it. Distinguishable from any real HTTP status, so a
 * caller can tell "you are offline" from "the server said no".
 */
export const EDGE_NETWORK_ERROR_STATUS = 0;

/**
 * Stable error tokens, NOT display copy.
 *
 * Several call sites render `result.error` verbatim, so returning an English
 * sentence here would put untranslated text in front of Spanish users. Callers
 * map these to i18n (`errors.network` / `errors.unexpected`) — or better, branch
 * on `status === EDGE_NETWORK_ERROR_STATUS`.
 */
export const EDGE_NETWORK_ERROR = 'NETWORK_ERROR';
export const EDGE_UNEXPECTED_ERROR = 'UNEXPECTED_ERROR';

/**
 * Calls a Supabase edge function and ALWAYS RESOLVES — never throws.
 *
 * That total contract is the point. This used to return `{ error }` for a
 * missing session and for any non-OK response, but *throw* on a network failure,
 * because `fetch` (and `getSession` / `refreshSession`) reject rather than
 * return. Every caller then had to remember the one exception, and three did
 * not — `useTeamMfaStatus`, `useEmployeeHandlers` and the platform's
 * `usePlatformTenantMfaStatus` all awaited it bare, so a dropped request escaped
 * as an unhandled rejection: a red overlay in dev, and in production a silently
 * hung UI plus Sentry noise. One contract removes the whole class.
 *
 * @returns the function's JSON on success, otherwise `{ error, status, code? }`.
 *          `status` is the HTTP status, or `EDGE_NETWORK_ERROR_STATUS` (0) when
 *          no response was received. EXCEPTION: the no-session return carries no
 *          `status` — callers that compare it numerically must tolerate
 *          `undefined` (`useSeatState` does, and retries). `code` is the edge
 *          function's own stable error token (e.g. `RATE_LIMITED`) when its JSON
 *          body carried one — omitted otherwise, so callers that only branch on
 *          `status`/`error` are unaffected.
 */
export async function callEdgeFunction(fnName, body, options = {}) {
  try {
    const { data } = await supabase.auth.getSession();
    let session = data?.session;
    if (!session) {
      return { error: 'No active session' };
    }
    // getSession() can hand back an expired access_token when the auto-refresh
    // timer was paused (hidden tab / device sleep). Sending a stale token makes
    // the edge function's getUser(token) reject as "Invalid token" (surfaced as a
    // catch-all 400). Refresh proactively at/near expiry so we send a live token.
    const nowSec = Math.floor(Date.now() / 1000);
    if (typeof session.expires_at === 'number' && session.expires_at - nowSec < 60) {
      const { data: refreshed } = await supabase.auth.refreshSession();
      if (refreshed?.session) session = refreshed.session;
    }
    const requestBody = JSON.stringify(options.debug ? { ...body, _debug: true } : body);
    let response;
    try {
      const request = fetch(`${supabaseUrl}/functions/v1/${fnName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': supabaseAnonKey,
        },
        body: requestBody,
      });
      // KAN-886: the silent app update must not reload mid-call (this fetch skips supabase.js).
      trackRequest('POST', request, `${supabaseUrl}/functions/v1/${fnName}`);
      response = await request;
    } catch (netErr) {
      // Scoped to the fetch ALONE so a bug of ours elsewhere in this function
      // can't masquerade as "you're offline" and skip reporting.
      return handleNetworkFailure(fnName, netErr);
    }
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      const msg = (result && typeof result.error === 'string' && result.error.trim())
        ? result.error
        : `Request failed (${response.status}). Please try again.`;
      try { const { reportApiError } = await import('../utils/error/errorReporting'); reportApiError(fnName, response.status, msg); } catch {}
      const code = (result && typeof result.code === 'string' && result.code) ? result.code : undefined;
      // KAN-519: raw fetch bypasses the supabase.js response hook.
      if (code === 'MFA_REQUIRED') emitMfaRequired();
      return { error: msg, status: response.status, ...(code ? { code } : {}) };
    }
    return result;
  } catch (err) {
    // NOT a transport failure — the auth client rejected, or something in this
    // function threw. That is our bug, so it is always reported: before this
    // wrapper existed these escaped as unhandled rejections and reached Sentry
    // via the global handler in index.js, and swallowing them silently would be
    // a net LOSS of signal.
    console.error(`[callEdgeFunction] ${fnName} failed unexpectedly`, err);
    reportEdgeFailure(fnName, err);
    return { error: EDGE_UNEXPECTED_ERROR, status: EDGE_NETWORK_ERROR_STATUS };
  }
}

/**
 * A dropped request: offline, DNS, CORS, aborted, or an iOS PWA resuming on a
 * dead connection.
 *
 * Reported ONLY when the browser believes it is online. That split is the whole
 * point: `navigator.onLine === false` is the user's connectivity and reporting
 * it would bury real signal in noise, while a `TypeError: Failed to fetch` while
 * ostensibly online is indistinguishable from a CORS/CSP regression or a blocked
 * origin — exactly the class we must not go blind to. Those unhandled rejections
 * used to reach Sentry on their own; this preserves that.
 */
function handleNetworkFailure(fnName, err) {
  console.error(`[callEdgeFunction] ${fnName} could not reach the server`, err);
  const seemsOnline = typeof navigator === 'undefined' || navigator.onLine !== false;
  if (seemsOnline) reportEdgeFailure(fnName, err);
  return { error: EDGE_NETWORK_ERROR, status: EDGE_NETWORK_ERROR_STATUS };
}

function reportEdgeFailure(fnName, err) {
  // Dynamic import keeps the error-reporting chain out of the cold-start bundle,
  // matching how the non-OK branch above reports.
  import('../utils/error/errorReporting')
    .then(({ reportApiError }) => reportApiError(fnName, EDGE_NETWORK_ERROR_STATUS, String(err?.message || err)))
    .catch(() => {});
}
